
// export default TruckMaintenance;
import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import toast, { toastError } from "../../utils/toast";
import FleetVehicleSelect from "./FleetVehicleSelect";
import {
  vmPageShell,
  vmTableScrollWrap,
  vmRecordRowBg,
  getVmRecordTableStyle,
  getVmRecordThStyle,
  getVmRecordTdStyle,
  vmRecordColGroupReact,
} from "./vehicleManagementLayout";
import { ViewMediaEyeAnchor } from "../../Components/ViewMediaEye";

const BASE_URL = API_BASE_URL;

const TABS = [
  "Regular Maintenance",
  "Oil Service",
  "Tyre Service",
  "Battery Service",
  "RTO / Document Expense",
  "Spare Parts Service",
  "Inventory Service",
  "Loan",
];

/** Label for the yellow button under tabs — always starts with “Add”. */
function addEntryButtonLabel(activeTab) {
  switch (activeTab) {
    case "Regular Maintenance":
      return "Add Entry";
    case "RTO / Document Expense":
      return "Add Documents";
    case "Spare Parts Service":
      return "Add Spare Parts";
    case "Inventory Service":
      return "Add Inventory Services";
    case "Loan":
      return "Add Loan Details";
    case "Oil Service":
    case "Tyre Service":
    case "Battery Service":
      return `Add ${activeTab}`;
    default:
      return activeTab ? `Add ${activeTab}` : "Add Entry";
  }
}

// ── All service type arrays (first empty = placeholder) ──────────────────────
const OIL_SERVICE_TYPES     = ["Oil Change","Hub Grease","Brake Pad","Brake Lining","CNG Filter","Coolant","Gear Box Oil","Differential Oil","Power Steering Oil","Hydraulic Oil","Air Filter","Fuel Filter","Clutch Plate","Housing","Other"];
const TYRE_SERVICE_TYPES    = ["New Tyre Fitment","Tyre Rotation","Tyre Repair / Puncture","Retreading","Balancing","Alignment","Tyre Removal","Other"];
const TYRE_POSITIONS        = ["Front 1 (Left)","Front 2 (Right)","Rear 1 (Left Outer)","Rear 2 (Left Inner)","Rear 3 (Right Inner)","Rear 4 (Right Outer)","Rear 5 - 2nd Axle Left Outer","Rear 6 - 2nd Axle Left Inner","Rear 7 - 2nd Axle Right Inner","Rear 8 - 2nd Axle Right Outer","Spare","Other"];
const TYRE_CONDITIONS       = ["New","Good","Worn","Retreaded","Damaged"];
const BATTERY_SERVICE_TYPES = ["New Battery","Battery Recharge","Battery Water Top-up","Terminal Cleaning","Battery Testing","Battery Replacement","Other"];
const RTO_TYPES             = ["Insurance","Fitness","Road Tax","Pollution","State Permit","National Permit","Temporary Permit","RTO Challan","Police Fine"];
const SPARE_CATEGORIES      = ["Engine Parts","Brake System","Suspension & Steering","Electrical & Lights","Transmission & Clutch","Cooling System","Exhaust System","Fuel System","Body Parts","Cabin & Interior","Tyres & Wheels","Filters","Belts & Hoses","Gaskets & Seals","Nuts, Bolts & Fasteners","Other"];
const INVENTORY_CATEGORIES  = ["Lubricants & Oils","Filters","Grease & Sealants","Cleaning Supplies","Workshop Tools","Safety Equipment","Spare Parts Stock","Fuel Additives","Consumables","Other"];
const REGULAR_TYPES         = ["Item Purchase","Mechanical Labour","Electrical Labour","Others"];
const RENEW_BY_OPTIONS      = ["Lavakumar N","Mallikarjun"];
const LOAN_TYPES            = ["","Commercial","Car Loan","Personal Loan","Other"];
const FINANCERS             = ["","HDB","Mahindra Finance","TMFL","Federal Bank","Axis Bank","Chola","HDFC Bank","Sundaram Finance","Other"];

/** Bank / txn / instrument fields only for Credit, UPI, or Card (not Cash). */
const NONCASH_PAYMENT_MODES = ["Credit", "UPI", "Card"];
const needsNonCashPaymentDetails = (mode) => NONCASH_PAYMENT_MODES.includes(mode);

const PAYMENT_INSTRUMENT_OPTIONS = {
  Credit: ["Supplier credit", "Bill to company", "Other"],
  UPI: ["Google Pay", "PhonePe", "Paytm", "BHIM", "Other UPI app"],
  Card: ["Credit card", "Debit card", "Corporate card"],
};

const emptyForm = {
  truckNo: "", kilometer: "", paymentMode: "Cash", bankName: "", accountNumber: "",
  paymentTxnRef: "", paymentInstrumentMode: "",
  supplierPartyName: "", amount: "", date: new Date().toISOString().split("T")[0],
  driver: "", remarks: "", documents: {},   // now an object: { labelKey: File }
  maintenanceType: "Regular Maintenance",

  // Regular Maintenance — multi-select
  approvedBy: "", maintenanceSubTypes: [], maintenanceSubTypeOther: "", workshopName: "",

  // Oil Service — multi-select
  oilServiceTypes: [], oilServiceTypeOther: "", serviceNo: "", nextServiceNo: "",
  nextServiceKM: "", nextServiceDate: "",
  oilApprovedBy: "", serviceCentre: "", advisorName: "", advisorContact: "",

  // Tyre Service — multi-select service types & positions
  tyreServiceTypes: [], tyreServiceTypeOther: "",
  tyrePositions: [], tyrePositionOther: "",
  tyreNo: "", tyreBrand: "", tyreSize: "", tyreQuantity: "", costPerTyre: "", tyreCondition: "",

  // Battery Service — multi-select
  batteryServiceTypes: [], batteryServiceTypeOther: "",
  batteryBrand: "", batteryCapacity: "", batterySerialNo: "", warrantyUpto: "",

  // RTO
  rtoExpenseTypes: [], rtoEntityName: "", renewBy: "", penaltyReason: "", penaltyAuthority: "",

  // Spare Parts — multi-select categories
  sparePartName: "", partNumber: "", partCategories: [], partCategoryOther: "", quantity: "", costPerUnit: "",

  // Inventory — multi-select categories
  inventoryType: "Purchase", materialDescription: "", inventoryQuantity: "", unit: "",
  inventoryCategories: [], inventoryCategoryOther: "",

  // Loan
  financer: "", financerOther: "", loanType: "", loanAmount: "", interestAmount: "", totalPayable: "",
  emiAmount: "", tenure: "", totalInstallments: "", paidAmount: "", pendingInstallments: "",
  outStanding: "",   loanStartDate: "", loanEndDate: "",
};

/** Record grid headers per tab — shared by thead and mobile column count */
function truckMaintenanceTableHeaders(activeTab) {
  switch (activeTab) {
    case "Regular Maintenance":
      return ["Date", "Vehicle No", "Driver", "Current KM", "Type", "Approved By", "Supplier / Workshop", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks", "Final Invoice"];
    case "Oil Service":
      return ["Date", "Vehicle No", "Driver Name", "Service No", "Current KM", "Service Work Type", "Workshop", "Advisor", "Contact", "Approved By", "Next Service No", "Next Service KM", "Next Service Date", "Remarks", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Final Invoice"];
    case "Tyre Service":
      return ["Date", "Vehicle No", "Driver", "KM", "Service Type", "Position", "Tyre No", "Brand", "Size", "Qty", "Cost/Tyre", "Condition", "Supplier", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks", "Final Invoice"];
    case "Battery Service":
      return ["Date", "Vehicle No", "Driver", "KM", "Service Type", "Brand", "Capacity", "Serial No", "Warranty", "Supplier", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks", "Final Invoice"];
    case "RTO / Document Expense":
      return ["Date", "Vehicle No", "Document Types", "Entity Name", "Renew By", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks"];
    case "Spare Parts Service":
      return ["Date", "Vehicle No", "Driver", "KM", "Part Name", "Part No", "Category", "Qty", "Cost/Unit", "Supplier", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks", "Final Invoice"];
    case "Inventory Service":
      return ["Date", "Type", "Category", "Material", "Qty", "Unit", "Supplier", "Amount (Rs.)", "Txn ref", "Payment", "Mode", "Bank", "Account", "Remarks"];
    case "Loan":
      return ["Vehicle No", "Financer", "Loan Type", "Loan Amt", "Interest", "Total Payable", "EMI", "Tenure", "Total Installments Paid", "Paid Amt", "Pending Instl", "Outstanding", "Start Date", "End Date", "Document"];
    default:
      return ["Date", "Vehicle No", "Amount (Rs.)", "Remarks"];
  }
}

function toYmdInput(val) {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? "" : new Date(t).toISOString().slice(0, 10);
}

/** Map a saved TruckMaintenance row into the add/edit form shape. */
function recordToFormData(rec) {
  const o = { ...emptyForm, documents: {} };
  Object.keys(emptyForm).forEach((key) => {
    if (key === "documents") return;
    const v = rec[key];
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      o[key] = [...v];
      return;
    }
    if (
      key === "date" ||
      key === "nextServiceDate" ||
      key === "warrantyUpto" ||
      key === "loanStartDate" ||
      key === "loanEndDate"
    ) {
      o[key] = toYmdInput(v);
      return;
    }
    if (typeof v === "number" && !Number.isNaN(v)) {
      o[key] = String(v);
      return;
    }
    if (typeof v === "object") return;
    o[key] = v;
  });
  o.maintenanceType = String(rec.maintenanceType || o.maintenanceType);
  o.truckNo = String(rec.truckNo || "");
  return o;
}

// ── Multi-select pill dropdown component ────────────────────────────────────
const MultiSelectDropdown = ({ label, options, selected = [], onChange, placeholder, style = {} }) => {
  const [open, setOpen] = useState(false);

  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(o => o !== opt));
    else onChange([...selected, opt]);
  };

  const displayText = selected.length === 0
    ? placeholder || "Select..."
    : selected.length <= 2
      ? selected.join(", ")
      : `${selected.slice(0, 2).join(", ")} +${selected.length - 2} more`;

  return (
    <div style={{ position: "relative", ...style }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: "8px 10px", border: "1.5px solid #000", borderRadius: "4px",
          fontSize: "14px", cursor: "pointer", backgroundColor: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          userSelect: "none", fontFamily: "Arial, sans-serif",
          color: selected.length ? "#000" : "#999",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {displayText}
        </span>
        <span style={{ marginLeft: 8, fontSize: 10 }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{
          position: "absolute", zIndex: 999, top: "100%", left: 0, right: 0,
          backgroundColor: "#fff", border: "1.5px solid #000", borderRadius: "4px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: 220, overflowY: "auto",
        }}>
          {options.map(opt => {
            const sel = selected.includes(opt);
            return (
              <div
                key={opt}
                onClick={() => toggle(opt)}
                style={{
                  padding: "9px 12px", cursor: "pointer", fontSize: "13px",
                  fontFamily: "Arial, sans-serif",
                  backgroundColor: sel ? "#FFC107" : "#fff",
                  fontWeight: sel ? "bold" : "normal",
                  display: "flex", alignItems: "center", gap: 8,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <span style={{
                  width: 16, height: 16, border: "1.5px solid #000", borderRadius: 3,
                  backgroundColor: sel ? "#000" : "#fff", display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {sel && <span style={{ color: "#FFC107", fontSize: 11, fontWeight: "bold" }}>✓</span>}
                </span>
                {opt}
              </div>
            );
          })}
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {open && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 998 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// ── Document upload section based on selected types ─────────────────────────
const DocumentUploadSection = ({ selectedTypes, documents, onDocumentChange, inputStyle }) => {
  if (!selectedTypes || selectedTypes.length === 0) return null;

  return (
    <>
      {selectedTypes.map((type) => {
        const key = `doc_${type.replace(/\s+/g, "_")}`;
        return (
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: "1 / -1" }}>
            <label style={{ fontWeight: "bold", fontSize: 13, color: "#000" }}>
              Upload Document — <span style={{ color: "#c00" }}>{type}</span>
              <span style={{ fontWeight: "normal", color: "#666", marginLeft: 6 }}>(PDF / Image)</span>
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => onDocumentChange(key, e.target.files[0])}
              style={{ fontSize: 14, fontFamily: "Arial, sans-serif", maxWidth: "100%" }}
            />
            {documents[key] && (
              <span style={{ fontSize: 12, color: "#555" }}>
                ✓ {documents[key].name}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
};

/** Stable module components — avoids remount-on-keystroke when defined inside TruckMaintenance (focus loss). */
function MaintenanceTruckNumberSelect({ formData, handleChange, S }) {
  return (
    <div style={S.formGroup}>
      <label style={S.label}>Vehicle Number</label>
      <FleetVehicleSelect name="truckNo" value={formData.truckNo} onChange={handleChange} style={S.select} />
    </div>
  );
}

function MaintenanceVehicleBlock({ kilometerLabel = "Kilometre", formData, handleChange, S }) {
  return (
    <>
      <div style={S.sectionDivider}>Vehicle & Driver Details</div>
      <div style={S.formGroup}>
        <label style={S.label}>Date</label>
        <input style={S.input} type="date" name="date" value={formData.date} onChange={handleChange} />
      </div>
      <MaintenanceTruckNumberSelect formData={formData} handleChange={handleChange} S={S} />
      <div style={S.formGroup}>
        <label style={S.label}>Driver Name</label>
        <input style={S.input} name="driver" value={formData.driver} onChange={handleChange} placeholder="Driver name" />
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>{kilometerLabel}</label>
        <input style={S.input} type="number" name="kilometer" value={formData.kilometer} onChange={handleChange} placeholder="e.g. 45000" />
      </div>
    </>
  );
}

function MaintenancePaymentBlock({ showSupplier = false, formData, handleChange, S, C }) {
  const digital = needsNonCashPaymentDetails(formData.paymentMode);
  const modeOptions = PAYMENT_INSTRUMENT_OPTIONS[formData.paymentMode] || [];
  return (
    <>
      <div style={S.sectionDivider}>Payment Details</div>
      {showSupplier && (
        <div style={S.formGroup}>
          <label style={S.label}>Supplier / Party Name</label>
          <input style={S.input} name="supplierPartyName" value={formData.supplierPartyName} onChange={handleChange} placeholder="Supplier or party name" />
        </div>
      )}
      <div style={S.formGroup}>
        <label style={S.label}>Amount (Rs.)</label>
        <input
          style={S.input}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          placeholder="0"
        />
      </div>
      {digital && (
        <div style={S.formGroup}>
          <label style={S.label}>Txn / ref. no.</label>
          <input style={S.input} name="paymentTxnRef" value={formData.paymentTxnRef} onChange={handleChange} placeholder="UPI ref, RRNo, auth code…" />
        </div>
      )}
      <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
        <label style={S.label}>Payment mode</label>
        <div style={S.radioRow}>
          {["Cash", "Credit", "UPI", "Card"].map((m) => (
            <label key={m} style={{ fontSize: C.fontSize, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontFamily: C.fontFamily }}>
              <input type="radio" name="paymentMode" value={m} checked={formData.paymentMode === m} onChange={handleChange} /> {m}
            </label>
          ))}
        </div>
      </div>
      {digital && (
        <>
          <div style={S.formGroup}>
            <label style={S.label}>Mode</label>
            <select
              style={S.select}
              name="paymentInstrumentMode"
              value={formData.paymentInstrumentMode}
              onChange={handleChange}
            >
              <option value="">Select mode</option>
              {modeOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Bank name</label>
            <input style={S.input} name="bankName" value={formData.bankName} onChange={handleChange} placeholder="e.g. HDFC, SBI" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Account number</label>
            <input style={S.input} name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="Account number" />
          </div>
        </>
      )}
    </>
  );
}

const TruckMaintenance = () => {
  const [activeTab,   setActiveTab]   = useState("Regular Maintenance");
  const [showForm,    setShowForm]    = useState(false);
  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [searchParty, setSearchParty] = useState("");
  const [formData,    setFormData]    = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [editUploadSnapshot, setEditUploadSnapshot] = useState("{}");
  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const C = {
    yellow: "#FFC107", black: "#000000", white: "#ffffff",
    border: "1.5px solid #000000", borderLight: "1px solid #f0f0f0",
    radius: "4px", fontFamily: "Arial, sans-serif",
    fontSize: "14px", fontSizeSm: "13px",
  };

  const S = {
    container: {
      ...vmPageShell(isMobile),
      fontFamily: C.fontFamily,
      backgroundColor: C.white, color: C.black,
      minHeight: "calc(100vh - 70px)",
      boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    },
    pageHeader: {
      backgroundColor: C.yellow, color: C.black, padding: "16px 20px",
      fontWeight: "bold", fontSize: "20px", letterSpacing: "1px", textTransform: "uppercase",
    },
    innerPad: { padding: isMobile ? "10px" : "20px" },
    btnYellow: { padding: "9px 20px", border: "none", borderRadius: C.radius, cursor: "pointer", backgroundColor: C.yellow, color: C.black, fontWeight: "bold", fontSize: C.fontSize, fontFamily: C.fontFamily, whiteSpace: "nowrap" },
    btnBlack:  { padding: "9px 20px", border: "none", borderRadius: C.radius, cursor: "pointer", backgroundColor: C.black, color: C.white, fontWeight: "bold", fontSize: C.fontSize, fontFamily: C.fontFamily, whiteSpace: "nowrap" },
    tabScroll: {
      display: "flex", borderBottom: "2px solid #e0a800", marginBottom: "16px",
      overflowX: "auto", WebkitOverflowScrolling: "touch", flexWrap: "nowrap",
      scrollbarWidth: "none", msOverflowStyle: "none",
    },
    tab: (active) => ({
      padding: isMobile ? "9px 10px" : "10px 18px", cursor: "pointer",
      fontSize: isMobile ? "11px" : C.fontSizeSm,
      fontWeight: active ? "bold" : "normal",
      color: active ? C.black : "#555",
      backgroundColor: active ? C.yellow : "transparent",
      border: "none", outline: "none", borderRadius: "4px 4px 0 0",
      whiteSpace: "nowrap", flexShrink: 0, fontFamily: C.fontFamily,
    }),
    searchRow: { display: "flex", gap: "10px", marginBottom: "14px", alignItems: "center", flexWrap: "wrap" },
    searchInput: {
      padding: "8px 12px", border: C.border, borderRadius: C.radius, fontSize: C.fontSize,
      width: isMobile ? "100%" : "280px", boxSizing: "border-box",
      color: C.black, fontFamily: C.fontFamily,
    },
    table: getVmRecordTableStyle(isMobile, truckMaintenanceTableHeaders(activeTab).length),
    th: getVmRecordThStyle(isMobile),
    td: { ...getVmRecordTdStyle(isMobile), color: C.black },
    formSection: {
      border: C.border,
      borderRadius: C.radius,
      marginBottom: "20px",
      overflow: "hidden",
      backgroundColor: C.white,
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    },
    formHeader: {
      backgroundColor: C.yellow,
      color: C.black,
      padding: "12px 16px",
      fontWeight: "bold",
      fontSize: C.fontSize,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
    },
    formHeaderClose: {
      background: "none",
      border: "none",
      fontSize: "18px",
      lineHeight: 1,
      cursor: "pointer",
      color: C.black,
      fontFamily: C.fontFamily,
      padding: "4px 8px",
      minHeight: 44,
      minWidth: 44,
      flexShrink: 0,
      touchAction: "manipulation",
    },
    formBody: { padding: isMobile ? "12px" : "20px" },
    sectionDivider: {
      fontWeight: "bold", fontSize: "12px", color: "#555", textTransform: "uppercase",
      letterSpacing: "0.5px", borderBottom: "1px solid #e0e0e0", paddingBottom: "6px",
      marginBottom: "14px", marginTop: "18px", gridColumn: "1 / -1",
    },
    formGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
    label: { fontWeight: "bold", fontSize: C.fontSizeSm, color: C.black },
    input: {
      padding: "8px 10px", border: C.border, borderRadius: C.radius, fontSize: C.fontSize,
      color: C.black, backgroundColor: C.white, outline: "none",
      width: "100%", boxSizing: "border-box", fontFamily: C.fontFamily, minWidth: 0,
    },
    select: {
      padding: "8px 10px", border: C.border, borderRadius: C.radius, fontSize: C.fontSize,
      color: C.black, backgroundColor: C.white, outline: "none",
      width: "100%", boxSizing: "border-box", fontFamily: C.fontFamily, minWidth: 0,
    },
    radioRow: { display: "flex", gap: isMobile ? "10px" : "16px", alignItems: "center", paddingTop: "6px", flexWrap: "wrap" },
    btnRow: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px", flexWrap: "wrap" },
    fullCol: { gridColumn: "1 / -1" },
    rtoCheckboxWrap: { display: "flex", flexWrap: "wrap", gap: "8px", paddingTop: "6px" },
    rtoCheckboxLabel: (selected) => ({
      display: "flex", alignItems: "center", gap: 6,
      fontSize: isMobile ? "12px" : C.fontSize, cursor: "pointer", fontFamily: C.fontFamily,
      padding: isMobile ? "5px 10px" : "6px 12px",
      border: selected ? `2px solid ${C.black}` : "1.5px solid #ccc",
      borderRadius: C.radius,
      backgroundColor: selected ? C.yellow : C.white,
      fontWeight: selected ? "bold" : "normal", whiteSpace: "nowrap", userSelect: "none",
    }),
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/vehicle/truck-maintenance`, { withCredentials: true });
      setRecords(res.data || []);
    } catch { setRecords([]); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
      let v = String(value ?? "").replace(/[^\d.]/g, "");
      const dot = v.indexOf(".");
      if (dot !== -1) {
        v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
      }
      setFormData((p) => ({ ...p, amount: v }));
      return;
    }
    if (name === "paymentMode") {
      if (value === "Cash") {
        setFormData((p) => ({
          ...p,
          paymentMode: value,
          bankName: "",
          accountNumber: "",
          paymentTxnRef: "",
          paymentInstrumentMode: "",
        }));
      } else {
        setFormData((p) => ({
          ...p,
          paymentMode: value,
          paymentInstrumentMode: "",
        }));
      }
      return;
    }
    setFormData(p => ({ ...p, [name]: value }));
  };

  // Multi-select array toggle
  const toggleArrayField = (fieldName, value) => {
    setFormData(p => {
      const arr = p[fieldName] || [];
      return {
        ...p,
        [fieldName]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  };

  // Named document upload handler
  const handleDocumentChange = (key, file) => {
    setFormData(p => ({ ...p, documents: { ...p.documents, [key]: file } }));
  };

  const handleRtoTypeToggle = (type) => toggleArrayField("rtoExpenseTypes", type);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData({ ...emptyForm, maintenanceType: tab });
    setShowForm(false);
    setEditingId(null);
    setEditUploadSnapshot("{}");
  };

  /** Opens the entry form for the currently selected tab (fresh empty form). */
  const openEntryForm = () => {
    setEditingId(null);
    setEditUploadSnapshot("{}");
    setFormData({ ...emptyForm, maintenanceType: activeTab });
    setShowForm(true);
  };

  const beginEditMaintenance = (rec) => {
    if (!rec?._id) return;
    const mt = String(rec.maintenanceType || activeTab);
    setActiveTab(mt);
    setFormData(recordToFormData(rec));
    setEditUploadSnapshot(
      JSON.stringify(
        rec.uploadedDocuments && typeof rec.uploadedDocuments === "object" && !Array.isArray(rec.uploadedDocuments)
          ? rec.uploadedDocuments
          : {},
      ),
    );
    setEditingId(String(rec._id));
    setShowForm(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === "documents") {
          Object.entries(v || {}).forEach(([docKey, file]) => {
            if (file) payload.append(docKey, file);
          });
        } else if (Array.isArray(v)) {
          payload.append(k, JSON.stringify(v));
        } else if (v !== null && v !== undefined) {
          payload.append(k, v);
        }
      });

      if (editingId) {
        payload.append("uploadedDocuments", editUploadSnapshot);
        await axios.patch(`${BASE_URL}/vehicle/truck-maintenance/${editingId}`, payload, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast("Record updated successfully!");
      } else {
        await axios.post(`${BASE_URL}/vehicle/truck-maintenance`, payload, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast("Record saved successfully!");
      }
      setEditingId(null);
      setEditUploadSnapshot("{}");
      setFormData({ ...emptyForm, maintenanceType: activeTab });
      setShowForm(false);
      fetchRecords();
    } catch {
      toastError("Error saving record. Please check backend connection.");
    }
    finally { setLoading(false); }
  };

  const handleClear = () => {
    if (editingId) {
      const rec = records.find((r) => String(r._id) === editingId);
      if (rec) {
        setFormData(recordToFormData(rec));
        setEditUploadSnapshot(
          JSON.stringify(
            rec.uploadedDocuments &&
              typeof rec.uploadedDocuments === "object" &&
              !Array.isArray(rec.uploadedDocuments)
              ? rec.uploadedDocuments
              : {},
          ),
        );
      } else {
        setEditingId(null);
        setEditUploadSnapshot("{}");
        setFormData({ ...emptyForm, maintenanceType: activeTab });
      }
      return;
    }
    setFormData({ ...emptyForm, maintenanceType: activeTab });
  };

  const uploadedDocHref = (rec, fieldKey) => {
    const docs = rec.uploadedDocuments;
    const raw = docs && typeof docs === "object" ? docs[fieldKey] : null;
    if (!raw || typeof raw !== "string") return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const pathPart = raw.replace(/^\/?uploads\/?/, "");
    return `${BASE_URL}/uploads/${pathPart}`;
  };

  const filteredRecords = records.filter((r) => {
    if (r.maintenanceType !== activeTab) return false;
    if (!searchParty.trim()) return true;
    const q = searchParty.toLowerCase();
    const hay = [
      r.supplierPartyName,
      r.rtoEntityName,
      r.truckNo,
      r.driver,
      r.remarks,
      r.serviceNo,
      r.nextServiceNo,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  // ── Tab-specific fields (vehicle / payment sections use stable module-scope components above) ──
  const renderTabFields = () => {
    switch (activeTab) {

      case "Regular Maintenance":
        return (
          <>
            <MaintenanceVehicleBlock kilometerLabel="Current KM" formData={formData} handleChange={handleChange} S={S} />
            <div style={S.sectionDivider}>Work Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Work Approved By</label>
              <input style={S.input} name="approvedBy" value={formData.approvedBy} onChange={handleChange} placeholder="Approver name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Type (select all that apply)</label>
              <MultiSelectDropdown
                options={REGULAR_TYPES}
                selected={formData.maintenanceSubTypes}
                onChange={(vals) =>
                  setFormData((p) => {
                    const nextDocs = { ...p.documents };
                    REGULAR_TYPES.forEach((t) => {
                      delete nextDocs[`doc_${t.replace(/\s+/g, "_")}`];
                    });
                    return { ...p, maintenanceSubTypes: vals, documents: nextDocs };
                  })}
                placeholder="Select Type(s)"
              />
            </div>
            {formData.maintenanceSubTypes.includes("Others") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Type</label>
                <input style={S.input} name="maintenanceSubTypeOther" value={formData.maintenanceSubTypeOther} onChange={handleChange} placeholder="Describe the work..." />
              </div>
            )}
            {formData.maintenanceSubTypes.includes("Item Purchase") && (
              <div style={S.formGroup}>
                <label style={S.label}>Supplier / Party Details</label>
                <input style={S.input} name="supplierPartyName" value={formData.supplierPartyName} onChange={handleChange} placeholder="Supplier or party name" />
              </div>
            )}
            {(formData.maintenanceSubTypes.includes("Mechanical Labour") || formData.maintenanceSubTypes.includes("Electrical Labour")) && (
              <div style={S.formGroup}>
                <label style={S.label}>Workshop Name</label>
                <input style={S.input} name="workshopName" value={formData.workshopName} onChange={handleChange} placeholder="Workshop name" />
              </div>
            )}
            <MaintenancePaymentBlock formData={formData} handleChange={handleChange} S={S} C={C} />
            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Final Invoice (PDF / Image)</label>
              <span style={{ fontSize: 11, color: "#555", display: "block", marginBottom: 4 }}>
                One invoice for this visit — covers all selected work types.
              </span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentChange("doc_final_invoice", e.target.files[0])}
                style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
              />
              {formData.documents.doc_final_invoice && (
                <span style={{ fontSize: 12, color: "#555" }}>✓ {formData.documents.doc_final_invoice.name}</span>
              )}
            </div>
          </>
        );

      case "Oil Service":
        return (
          <>
            {/* Order: Date → Vehicle → Driver → Service No → Current KM → work type → workshop → … */}
            <div style={S.sectionDivider}>Vehicle Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Date</label>
              <input style={S.input} type="date" name="date" value={formData.date} onChange={handleChange} />
            </div>
            <MaintenanceTruckNumberSelect formData={formData} handleChange={handleChange} S={S} />
            <div style={S.formGroup}>
              <label style={S.label}>Driver Name</label>
              <input style={S.input} name="driver" value={formData.driver} onChange={handleChange} placeholder="Driver name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Service No</label>
              <input style={S.input} name="serviceNo" value={formData.serviceNo} onChange={handleChange} placeholder="e.g. OS-2026-001" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Current KM</label>
              <input style={S.input} type="number" name="kilometer" value={formData.kilometer} onChange={handleChange} placeholder="e.g. 45000" />
            </div>

            <div style={S.sectionDivider}>Service Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Service Work Type (select all that apply)</label>
              <MultiSelectDropdown
                options={OIL_SERVICE_TYPES}
                selected={formData.oilServiceTypes}
                onChange={(vals) =>
                  setFormData((p) => {
                    const nextDocs = { ...p.documents };
                    OIL_SERVICE_TYPES.forEach((t) => {
                      delete nextDocs[`doc_${t.replace(/\s+/g, "_")}`];
                    });
                    return { ...p, oilServiceTypes: vals, documents: nextDocs };
                  })}
                placeholder="Select service type(s)"
              />
            </div>
            {formData.oilServiceTypes.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Service</label>
                <input style={S.input} name="oilServiceTypeOther" value={formData.oilServiceTypeOther} onChange={handleChange} placeholder="Describe the service..." />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Service Centre / Workshop Name</label>
              <input style={S.input} name="serviceCentre" value={formData.serviceCentre} onChange={handleChange} placeholder="Workshop or service centre name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Advisor Name</label>
              <input style={S.input} name="advisorName" value={formData.advisorName} onChange={handleChange} placeholder="Service advisor name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Contact Number</label>
              <input style={S.input} name="advisorContact" value={formData.advisorContact} onChange={handleChange} placeholder="Advisor contact number" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Approved By</label>
              <input style={S.input} name="oilApprovedBy" value={formData.oilApprovedBy} onChange={handleChange} placeholder="Approver name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Next Service No</label>
              <input style={S.input} name="nextServiceNo" value={formData.nextServiceNo} onChange={handleChange} placeholder="e.g. OS-2026-002" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Next Service KM</label>
              <input style={S.input} type="number" name="nextServiceKM" value={formData.nextServiceKM} onChange={handleChange} placeholder="e.g. 50000" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Next Service Date (dd/mm/yyyy)</label>
              <input style={S.input} type="date" name="nextServiceDate" value={formData.nextServiceDate} onChange={handleChange} />
            </div>

            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Remarks</label>
              <input style={S.input} name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Enter remark" />
            </div>

            <div style={S.sectionDivider}>Payment Details</div>
            <MaintenancePaymentBlock formData={formData} handleChange={handleChange} S={S} C={C} />

            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Final Invoice (PDF / Image)</label>
              <span style={{ fontSize: 11, color: "#555", display: "block", marginBottom: 4 }}>
                One invoice for this visit — covers all selected service work types.
              </span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentChange("doc_final_invoice", e.target.files[0])}
                style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
              />
              {formData.documents.doc_final_invoice && (
                <span style={{ fontSize: 12, color: "#555" }}>✓ {formData.documents.doc_final_invoice.name}</span>
              )}
            </div>
          </>
        );

      case "Tyre Service":
        return (
          <>
            <MaintenanceVehicleBlock formData={formData} handleChange={handleChange} S={S} />
            <div style={S.sectionDivider}>Tyre Service Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Service Type (select all that apply)</label>
              <MultiSelectDropdown
                options={TYRE_SERVICE_TYPES}
                selected={formData.tyreServiceTypes}
                onChange={(vals) =>
                  setFormData((p) => {
                    const nextDocs = { ...p.documents };
                    TYRE_SERVICE_TYPES.forEach((t) => {
                      delete nextDocs[`doc_${t.replace(/\s+/g, "_")}`];
                    });
                    return { ...p, tyreServiceTypes: vals, documents: nextDocs };
                  })}
                placeholder="Select Service Type(s)"
              />
            </div>
            {formData.tyreServiceTypes.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Service</label>
                <input style={S.input} name="tyreServiceTypeOther" value={formData.tyreServiceTypeOther} onChange={handleChange} placeholder="Describe..." />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Tyre Position (select all that apply)</label>
              <MultiSelectDropdown
                options={TYRE_POSITIONS}
                selected={formData.tyrePositions}
                onChange={vals => setFormData(p => ({ ...p, tyrePositions: vals }))}
                placeholder="Select Position(s)"
              />
            </div>
            {formData.tyrePositions.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Position</label>
                <input style={S.input} name="tyrePositionOther" value={formData.tyrePositionOther} onChange={handleChange} placeholder="e.g. Trailer Axle 1 Left" />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Tyre Condition</label>
              <MultiSelectDropdown
                options={TYRE_CONDITIONS}
                selected={formData.tyreCondition ? [formData.tyreCondition] : []}
                onChange={vals => setFormData(p => ({ ...p, tyreCondition: vals[vals.length - 1] || "" }))}
                placeholder="Select Condition"
              />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Tyre No / Serial</label>
              <input style={S.input} name="tyreNo" value={formData.tyreNo} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Tyre Brand</label>
              <input style={S.input} name="tyreBrand" value={formData.tyreBrand} onChange={handleChange} placeholder="e.g. MRF, Apollo, CEAT" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Tyre Size</label>
              <input style={S.input} name="tyreSize" value={formData.tyreSize} onChange={handleChange} placeholder="e.g. 10.00 R20" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Quantity</label>
              <input style={S.input} type="number" name="tyreQuantity" value={formData.tyreQuantity} onChange={handleChange} placeholder="1" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Cost Per Tyre (Rs.)</label>
              <input style={S.input} type="number" name="costPerTyre" value={formData.costPerTyre} onChange={handleChange} placeholder="0" />
            </div>
            <MaintenancePaymentBlock showSupplier formData={formData} handleChange={handleChange} S={S} C={C} />
            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Final Invoice (PDF / Image)</label>
              <span style={{ fontSize: 11, color: "#555", display: "block", marginBottom: 4 }}>
                One invoice for this visit — covers all selected tyre service types.
              </span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentChange("doc_final_invoice", e.target.files[0])}
                style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
              />
              {formData.documents.doc_final_invoice && (
                <span style={{ fontSize: 12, color: "#555" }}>✓ {formData.documents.doc_final_invoice.name}</span>
              )}
            </div>
          </>
        );

      case "Battery Service":
        return (
          <>
            <MaintenanceVehicleBlock formData={formData} handleChange={handleChange} S={S} />
            <div style={S.sectionDivider}>Battery Service Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Service Type (select all that apply)</label>
              <MultiSelectDropdown
                options={BATTERY_SERVICE_TYPES}
                selected={formData.batteryServiceTypes}
                onChange={(vals) =>
                  setFormData((p) => {
                    const nextDocs = { ...p.documents };
                    BATTERY_SERVICE_TYPES.forEach((t) => {
                      delete nextDocs[`doc_${t.replace(/\s+/g, "_")}`];
                    });
                    return { ...p, batteryServiceTypes: vals, documents: nextDocs };
                  })}
                placeholder="Select Service Type(s)"
              />
            </div>
            {formData.batteryServiceTypes.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Service</label>
                <input style={S.input} name="batteryServiceTypeOther" value={formData.batteryServiceTypeOther} onChange={handleChange} placeholder="Describe..." />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Battery Brand</label>
              <input style={S.input} name="batteryBrand" value={formData.batteryBrand} onChange={handleChange} placeholder="e.g. Amaron, Exide, Luminous" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Capacity (Ah)</label>
              <input style={S.input} name="batteryCapacity" value={formData.batteryCapacity} onChange={handleChange} placeholder="e.g. 180Ah" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Serial No</label>
              <input style={S.input} name="batterySerialNo" value={formData.batterySerialNo} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Warranty Upto</label>
              <input style={S.input} type="date" name="warrantyUpto" value={formData.warrantyUpto} onChange={handleChange} />
            </div>
            <MaintenancePaymentBlock showSupplier formData={formData} handleChange={handleChange} S={S} C={C} />
            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Final Invoice (PDF / Image)</label>
              <span style={{ fontSize: 11, color: "#555", display: "block", marginBottom: 4 }}>
                One invoice for this visit — covers all selected battery service types.
              </span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentChange("doc_final_invoice", e.target.files[0])}
                style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
              />
              {formData.documents.doc_final_invoice && (
                <span style={{ fontSize: 12, color: "#555" }}>✓ {formData.documents.doc_final_invoice.name}</span>
              )}
            </div>
          </>
        );

      case "RTO / Document Expense":
        return (
          <>
            <div style={S.sectionDivider}>Document Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Date</label>
              <input style={S.input} type="date" name="date" value={formData.date} onChange={handleChange} />
            </div>
            <MaintenanceTruckNumberSelect formData={formData} handleChange={handleChange} S={S} />

            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Document Type (Select all that apply)</label>
              <div style={S.rtoCheckboxWrap}>
                {RTO_TYPES.map(type => {
                  const selected = (formData.rtoExpenseTypes || []).includes(type);
                  return (
                    <label key={type} style={S.rtoCheckboxLabel(selected)}>
                      <input type="checkbox" checked={selected} onChange={() => handleRtoTypeToggle(type)} style={{ display: "none" }} />
                      {type}
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Entity / Partner Name</label>
              <input style={S.input} name="rtoEntityName" value={formData.rtoEntityName} onChange={handleChange} placeholder="Entity or partner name" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Renew By</label>
              <select style={S.select} name="renewBy" value={formData.renewBy} onChange={handleChange}>
                <option value="">Select Person</option>
                {RENEW_BY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {(formData.rtoExpenseTypes || []).some(t => t === "RTO Challan" || t === "Police Fine") && (
              <>
                <div style={S.formGroup}>
                  <label style={S.label}>Penalty Reason</label>
                  <input style={S.input} name="penaltyReason" value={formData.penaltyReason} onChange={handleChange} placeholder="Reason for penalty" />
                </div>
                <div style={S.formGroup}>
                  <label style={S.label}>Authority / Officer</label>
                  <input style={S.input} name="penaltyAuthority" value={formData.penaltyAuthority} onChange={handleChange} placeholder="e.g. RTO Officer, Traffic Police" />
                </div>
              </>
            )}

            <MaintenancePaymentBlock formData={formData} handleChange={handleChange} S={S} C={C} />

            {/* One upload per selected RTO document type */}
            <DocumentUploadSection
              selectedTypes={formData.rtoExpenseTypes}
              documents={formData.documents}
              onDocumentChange={handleDocumentChange}
            />
          </>
        );

      case "Spare Parts Service":
        return (
          <>
            <MaintenanceVehicleBlock formData={formData} handleChange={handleChange} S={S} />
            <div style={S.sectionDivider}>Spare Part Details</div>
            <div style={S.formGroup}>
              <label style={S.label}>Part Category (select all that apply)</label>
              <MultiSelectDropdown
                options={SPARE_CATEGORIES}
                selected={formData.partCategories}
                onChange={(vals) =>
                  setFormData((p) => {
                    const nextDocs = { ...p.documents };
                    SPARE_CATEGORIES.forEach((t) => {
                      delete nextDocs[`doc_${t.replace(/\s+/g, "_")}`];
                    });
                    return { ...p, partCategories: vals, documents: nextDocs };
                  })}
                placeholder="Select Category(s)"
              />
            </div>
            {formData.partCategories.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Category</label>
                <input style={S.input} name="partCategoryOther" value={formData.partCategoryOther} onChange={handleChange} placeholder="Enter category..." />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Spare Part Name</label>
              <input style={S.input} name="sparePartName" value={formData.sparePartName} onChange={handleChange} placeholder="e.g. Brake Pad, Air Filter" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Part Number</label>
              <input style={S.input} name="partNumber" value={formData.partNumber} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Quantity</label>
              <input style={S.input} type="number" name="quantity" value={formData.quantity} onChange={handleChange} placeholder="1" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Cost Per Unit (Rs.)</label>
              <input style={S.input} type="number" name="costPerUnit" value={formData.costPerUnit} onChange={handleChange} placeholder="0" />
            </div>
            <MaintenancePaymentBlock showSupplier formData={formData} handleChange={handleChange} S={S} C={C} />
            <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
              <label style={S.label}>Final Invoice (PDF / Image)</label>
              <span style={{ fontSize: 11, color: "#555", display: "block", marginBottom: 4 }}>
                One invoice for this visit — covers all selected part categories.
              </span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentChange("doc_final_invoice", e.target.files[0])}
                style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
              />
              {formData.documents.doc_final_invoice && (
                <span style={{ fontSize: 12, color: "#555" }}>✓ {formData.documents.doc_final_invoice.name}</span>
              )}
            </div>
          </>
        );

      case "Inventory Service":
        return (
          <>
            <div style={S.sectionDivider}>Inventory Details</div>
            <div style={{ gridColumn: "1 / -1", display:"flex", gap:"10px", marginBottom:"4px", flexWrap:"wrap" }}>
              {["Purchase","Consumption"].map(t => (
                <button key={t} type="button"
                  style={{ padding:"8px 24px", border: C.border, borderRadius: C.radius, cursor:"pointer", fontWeight:"bold", fontSize: C.fontSizeSm, fontFamily: C.fontFamily, backgroundColor: formData.inventoryType === t ? C.black : C.white, color: formData.inventoryType === t ? C.yellow : C.black }}
                  onClick={() => setFormData(p => ({ ...p, inventoryType: t }))}>
                  {t}
                </button>
              ))}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Date</label>
              <input style={S.input} type="date" name="date" value={formData.date} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Category (select all that apply)</label>
              <MultiSelectDropdown
                options={INVENTORY_CATEGORIES}
                selected={formData.inventoryCategories}
                onChange={vals => setFormData(p => ({ ...p, inventoryCategories: vals }))}
                placeholder="Select Category(s)"
              />
            </div>
            {formData.inventoryCategories.includes("Other") && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Category</label>
                <input style={S.input} name="inventoryCategoryOther" value={formData.inventoryCategoryOther} onChange={handleChange} placeholder="Enter category..." />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Material Description</label>
              <input style={S.input} name="materialDescription" value={formData.materialDescription} onChange={handleChange} placeholder="Item description" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Quantity</label>
              <input style={S.input} type="number" name="inventoryQuantity" value={formData.inventoryQuantity} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Unit</label>
              <select style={S.select} name="unit" value={formData.unit} onChange={handleChange}>
                <option value="">Select</option>
                {["Nos","Ltr","Kg","Pcs","Set","Box","Roll","Pair","Mtr"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <MaintenancePaymentBlock showSupplier formData={formData} handleChange={handleChange} S={S} C={C} />
            <DocumentUploadSection
              selectedTypes={formData.inventoryCategories}
              documents={formData.documents}
              onDocumentChange={handleDocumentChange}
            />
          </>
        );

      case "Loan":
        return (
          <>
            <div style={S.sectionDivider}>Vehicle & Loan Details</div>
            <MaintenanceTruckNumberSelect formData={formData} handleChange={handleChange} S={S} />
            <div style={S.formGroup}>
              <label style={S.label}>Financer Name</label>
              <select style={S.select} name="financer" value={formData.financer} onChange={handleChange}>
                {FINANCERS.map(o => <option key={o} value={o}>{o || "Select Financer"}</option>)}
              </select>
            </div>
            {formData.financer === "Other" && (
              <div style={S.formGroup}>
                <label style={S.label}>Specify Financer</label>
                <input style={S.input} name="financerOther" value={formData.financerOther} onChange={handleChange} placeholder="Financer name" />
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Loan Type</label>
              <select style={S.select} name="loanType" value={formData.loanType} onChange={handleChange}>
                {LOAN_TYPES.map(o => <option key={o} value={o}>{o || "Select Loan Type"}</option>)}
              </select>
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Loan Amount (Rs.)</label>
              <input style={S.input} type="number" name="loanAmount" value={formData.loanAmount} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Interest Amount (Rs.)</label>
              <input style={S.input} type="number" name="interestAmount" value={formData.interestAmount} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Total Payable (Rs.)</label>
              <input style={S.input} type="number" name="totalPayable" value={formData.totalPayable} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>EMI Amount (Rs.)</label>
              <input style={S.input} type="number" name="emiAmount" value={formData.emiAmount} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Tenure (Months)</label>
              <input style={S.input} type="number" name="tenure" value={formData.tenure} onChange={handleChange} placeholder="e.g. 36" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Total Installments Paid</label>
              <input style={S.input} type="number" name="totalInstallments" value={formData.totalInstallments} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Paid Amount (Rs.)</label>
              <input style={S.input} type="number" name="paidAmount" value={formData.paidAmount} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Pending Installments</label>
              <input style={S.input} type="number" name="pendingInstallments" value={formData.pendingInstallments} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Outstanding Amount (Rs.)</label>
              <input style={S.input} type="number" name="outStanding" value={formData.outStanding} onChange={handleChange} placeholder="0" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Start Date</label>
              <input style={S.input} type="date" name="loanStartDate" value={formData.loanStartDate} onChange={handleChange} />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>End Date</label>
              <input style={S.input} type="date" name="loanEndDate" value={formData.loanEndDate} onChange={handleChange} />
            </div>
          </>
        );

      default: return null;
    }
  };

  const getTableHeaders = () => truckMaintenanceTableHeaders(activeTab);

  const renderTableRow = (rec) => {
    const joinArr = (arr) => Array.isArray(arr) ? arr.join(", ") : (arr || "");
    switch (activeTab) {
      case "Regular Maintenance": {
        const inv = uploadedDocHref(rec, "doc_final_invoice");
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          rec.driver,
          rec.kilometer,
          joinArr(rec.maintenanceSubTypes) || (rec.maintenanceSubType === "Others" ? rec.maintenanceSubTypeOther : rec.maintenanceSubType),
          rec.approvedBy,
          rec.supplierPartyName || rec.workshopName,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
          inv ? (
            <ViewMediaEyeAnchor href={inv} ariaLabel="View document" title="View document in new tab" />
          ) : "—",
        ];
      }
      case "Oil Service": {
        const inv = uploadedDocHref(rec, "doc_final_invoice");
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          rec.driver,
          rec.serviceNo ?? "",
          rec.kilometer,
          joinArr(rec.oilServiceTypes) || (rec.oilServiceType === "Other" ? rec.oilServiceTypeOther : rec.oilServiceType),
          rec.serviceCentre,
          rec.advisorName,
          rec.advisorContact ?? "",
          rec.oilApprovedBy,
          rec.nextServiceNo ?? "",
          rec.nextServiceKM,
          rec.nextServiceDate ? new Date(rec.nextServiceDate).toLocaleDateString("en-IN") : "",
          rec.remarks,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName,
          rec.accountNumber ?? "",
          inv ? (
            <ViewMediaEyeAnchor href={inv} ariaLabel="View document" title="View document in new tab" />
          ) : "—",
        ];
      }
      case "Tyre Service": {
        const inv = uploadedDocHref(rec, "doc_final_invoice");
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          rec.driver,
          rec.kilometer,
          joinArr(rec.tyreServiceTypes) || (rec.tyreServiceType === "Other" ? rec.tyreServiceTypeOther : rec.tyreServiceType),
          joinArr(rec.tyrePositions) || (rec.tyrePosition === "Other" ? rec.tyrePositionOther : rec.tyrePosition),
          rec.tyreNo ?? "",
          rec.tyreBrand,
          rec.tyreSize,
          rec.tyreQuantity,
          rec.costPerTyre,
          rec.tyreCondition,
          rec.supplierPartyName,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
          inv ? (
            <ViewMediaEyeAnchor href={inv} ariaLabel="View document" title="View document in new tab" />
          ) : "—",
        ];
      }
      case "Battery Service": {
        const inv = uploadedDocHref(rec, "doc_final_invoice");
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          rec.driver,
          rec.kilometer,
          joinArr(rec.batteryServiceTypes) || (rec.batteryServiceType === "Other" ? rec.batteryServiceTypeOther : rec.batteryServiceType),
          rec.batteryBrand,
          rec.batteryCapacity,
          rec.batterySerialNo,
          rec.warrantyUpto ? new Date(rec.warrantyUpto).toLocaleDateString("en-IN") : "",
          rec.supplierPartyName,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
          inv ? (
            <ViewMediaEyeAnchor href={inv} ariaLabel="View document" title="View document in new tab" />
          ) : "—",
        ];
      }
      case "RTO / Document Expense":
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          joinArr(rec.rtoExpenseTypes),
          rec.rtoEntityName,
          rec.renewBy,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
        ];
      case "Spare Parts Service": {
        const inv = uploadedDocHref(rec, "doc_final_invoice");
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.truckNo,
          rec.driver,
          rec.kilometer,
          rec.sparePartName,
          rec.partNumber,
          joinArr(rec.partCategories) || (rec.partCategory === "Other" ? rec.partCategoryOther : rec.partCategory),
          rec.quantity,
          rec.costPerUnit,
          rec.supplierPartyName,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
          inv ? (
            <ViewMediaEyeAnchor href={inv} ariaLabel="View document" title="View document in new tab" />
          ) : "—",
        ];
      }
      case "Inventory Service":
        return [
          rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "",
          rec.inventoryType,
          joinArr(rec.inventoryCategories) || (rec.inventoryCategory === "Other" ? rec.inventoryCategoryOther : rec.inventoryCategory),
          rec.materialDescription,
          rec.inventoryQuantity,
          rec.unit,
          rec.supplierPartyName,
          rec.amount ? `Rs.${rec.amount}` : "",
          rec.paymentTxnRef ?? "",
          rec.paymentMode,
          rec.paymentInstrumentMode ?? "",
          rec.bankName ?? "",
          rec.accountNumber ?? "",
          rec.remarks,
        ];
      case "Loan": {
        const loanDoc = uploadedDocHref(rec, "doc_loan");
        return [
          rec.truckNo,
          rec.financer === "Other" ? rec.financerOther : rec.financer,
          rec.loanType,
          rec.loanAmount ? `Rs.${rec.loanAmount}` : "",
          rec.interestAmount ? `Rs.${rec.interestAmount}` : "",
          rec.totalPayable ? `Rs.${rec.totalPayable}` : "",
          rec.emiAmount ? `Rs.${rec.emiAmount}` : "",
          rec.tenure,
          rec.totalInstallments,
          rec.paidAmount ? `Rs.${rec.paidAmount}` : "",
          rec.pendingInstallments,
          rec.outStanding ? `Rs.${rec.outStanding}` : "",
          rec.loanStartDate ? new Date(rec.loanStartDate).toLocaleDateString("en-IN") : "",
          rec.loanEndDate ? new Date(rec.loanEndDate).toLocaleDateString("en-IN") : "",
          loanDoc ? (
            <ViewMediaEyeAnchor href={loanDoc} ariaLabel="View loan document" title="View document in new tab" />
          ) : "—",
        ];
      }
      default:
        return [rec.date ? new Date(rec.date).toLocaleDateString("en-IN") : "", rec.truckNo, rec.amount ? `Rs.${rec.amount}` : "", rec.remarks];
    }
  };

  return (
    <div style={S.container}>
      <div style={S.pageHeader}>VEHICLE MAINTENANCE</div>

      <div style={S.innerPad}>
        {/* Tabs — pick service type; Add Entry below opens the form for that tab */}
        <div style={S.tabScroll}>
          {TABS.map(tab => (
            <button key={tab} style={S.tab(activeTab === tab)} onClick={() => handleTabChange(tab)}>
              {tab}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 12, marginBottom: showForm ? 12 : 14 }}>
          <button
            type="button"
            style={{ ...S.btnYellow, ...(showForm ? { opacity: 0.85 } : {}) }}
            onClick={openEntryForm}
            disabled={showForm}
          >
            {addEntryButtonLabel(activeTab)}
          </button>
        </div>

        {/* Inline form — bordered card, yellow title bar + ✕ */}
        {showForm && (
          <div style={S.formSection}>
            <div style={S.formHeader}>
              <span>{editingId ? `Edit — ${activeTab}` : activeTab}</span>
              <button
                type="button"
                aria-label="Close form"
                title="Close form"
                disabled={loading}
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setEditUploadSnapshot("{}");
                  setFormData({ ...emptyForm, maintenanceType: activeTab });
                }}
                style={{
                  ...S.formHeaderClose,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>
            <div style={S.formBody}>
              <div style={S.formGrid}>
                {renderTabFields()}

                {/* Remarks — Oil Service tab includes remarks in-field order; skip duplicate */}
                {activeTab !== "Oil Service" && (
                  <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
                    <label style={S.label}>Remarks</label>
                    <input style={S.input} name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Enter remark" />
                  </div>
                )}

                {/* Loan tab: single generic document upload */}
                {activeTab === "Loan" && (
                  <div style={{ ...S.formGroup, gridColumn: "1 / -1" }}>
                    <label style={S.label}>Upload Support Document (PDF / Image)</label>
                    <input
                      type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => handleDocumentChange("doc_loan", e.target.files[0])}
                      style={{ fontSize: C.fontSize, fontFamily: C.fontFamily, maxWidth: "100%" }}
                    />
                  </div>
                )}
              </div>

              <div style={S.btnRow}>
                <button type="button" style={S.btnBlack} onClick={() => { setShowForm(false); setEditingId(null); setEditUploadSnapshot("{}"); setFormData({ ...emptyForm, maintenanceType: activeTab }); }}>Cancel</button>
                <button type="button" style={S.btnBlack} onClick={handleClear}>Clear</button>
                <button type="button" style={S.btnYellow} onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : editingId ? "Update record" : "Save Record"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div style={S.searchRow}>
          <input
            style={S.searchInput}
            placeholder="Search by party, vehicle or driver..."
            value={searchParty}
            onChange={e => setSearchParty(e.target.value)}
          />
        </div>

        {/* Records table */}
        <div style={vmTableScrollWrap}>
          <table style={S.table}>
            {isMobile ? vmRecordColGroupReact(getTableHeaders().length + 1) : null}
            <thead>
              <tr>
                {getTableHeaders().map((h, hi) => <th style={S.th} key={hi}>{h}</th>)}
                <th style={S.th}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? filteredRecords.map((rec, i) => (
                <tr key={rec._id || i} style={{ backgroundColor: vmRecordRowBg(i) }}>
                  {renderTableRow(rec).map((cell, ci) => (
                    <td key={ci} style={S.td}>{cell ?? ""}</td>
                  ))}
                  <td style={S.td}>
                    <button type="button" style={{ ...S.btnBlack, padding: "6px 12px", fontSize: 12 }} onClick={() => beginEditMaintenance(rec)}>
                      Edit
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={getTableHeaders().length + 1}
                    style={{ ...S.td, textAlign:"center", color:"#aaa", padding:"28px" }}>
                    No records found for this tab.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TruckMaintenance;